import { authenticatedFetch } from '../utils/authenticatedFetch';
import { configs } from '../configs';

export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export interface WeeklyStaffSetting {
  specificDaysOff?: DayOfWeek[];
  daysOffCount?: number;
  standardWeeklyHours?: number;
  standardDailyHours?: number;
}

export interface MonthlyStaffSetting {
  daysOffCount?: number;
  standardWeeklyHours?: number;
  standardDailyHours?: number;
}

export interface YearlyStaffSetting {
  daysOffCount?: number;
  standardWeeklyHours?: number;
  standardDailyHours?: number;
}

export interface StaffSetting {
  id: string;
  paidDayOffAllowance: number;
  paidDayOffWipePolicy: 'wipe_monthly' | 'wipe_yearly' | 'keep';
  fiscalYearStartMonth: number;
  standardWeeklyHours: number;
  standardDailyHours: number;
  
  // Hierarchical settings
  weekly?: WeeklyStaffSetting;
  monthly?: MonthlyStaffSetting;
  yearly?: YearlyStaffSetting;

  tenantId: string;
  createdAt: string;
  updatedAt: string;
}


class StaffSettingsService {
  private apiRoot = configs.apiRoot;

  async getSettings(): Promise<StaffSetting> {
    const response = await authenticatedFetch(`${this.apiRoot}/staff-settings`, {
      method: 'GET',
    });
    
    if (!response.ok) {
       const error = await response.json();
       throw new Error(error.message || `Failed to fetch staff settings: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  }

  async updateSettings(updates: Partial<StaffSetting>): Promise<StaffSetting> {
    const response = await authenticatedFetch(`${this.apiRoot}/staff-settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `Failed to update staff settings: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Resolves the effective settings based on priority and frequency:
   * Weekly (x52) > Monthly (x12) > Yearly (x1) > Base
   */
  getEffectiveSettings(settings: StaffSetting): { paidDayOffAllowance: number, standardWeeklyHours: number, standardDailyHours: number } {
    const weekly = settings.weekly || {};
    const monthly = settings.monthly || {};
    const yearly = settings.yearly || {};
    
    // Calculate Annual Paid Day Off Allowance
    let effectiveAllowance = settings.paidDayOffAllowance;
    
    if (weekly.specificDaysOff && weekly.specificDaysOff.length > 0) {
      effectiveAllowance = weekly.specificDaysOff.length * 52;
    } else if (weekly.daysOffCount !== undefined) {
      effectiveAllowance = weekly.daysOffCount * 52;
    } else if (monthly.daysOffCount !== undefined) {
      effectiveAllowance = monthly.daysOffCount * 12;
    } else if (yearly.daysOffCount !== undefined) {
      effectiveAllowance = yearly.daysOffCount;
    }

    // Calculate Standard Weekly Hours
    const effectiveHours = 
      weekly.standardWeeklyHours ?? 
      monthly.standardWeeklyHours ?? 
      yearly.standardWeeklyHours ?? 
      settings.standardWeeklyHours;

    // Calculate Standard Daily Hours
    const effectiveDailyHours =
      weekly.standardDailyHours ??
      monthly.standardDailyHours ??
      yearly.standardDailyHours ??
      settings.standardDailyHours;

    return {
      paidDayOffAllowance: effectiveAllowance,
      standardWeeklyHours: effectiveHours,
      standardDailyHours: effectiveDailyHours,
    };
  }
}

export const staffSettingsService = new StaffSettingsService();

